import React from 'react';
import { BriefcaseIcon, TrashIcon, PlusCircleIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import CustomTooltipComponent from './custom_tooltip_component';
import { getTotalWeeklyHours, formatHoursAndMinutes, getShiftDuration } from '../utils/workTimeCount';
import { PencilSquareIcon } from '@heroicons/react/24/outline';


const WeekCalendar = ({ daysOfWeek, shiftsByDay, onPlusCircleClick, onCancelShift, whNameByDay, readOnly = false, onEditDay, isCustomDayByDay = {}, }) => {
    const totalWeeklyHours = getTotalWeeklyHours(daysOfWeek, shiftsByDay);
    
    return (
        <div className="w-full overflow-x-auto my-8 rounded-t-md rounded-b-xl shadow-md">
            <table className="border border-gray-200 rounded-xl shadow-md w-full table-auto">
                <thead>
                    <tr>
                        {daysOfWeek.map((day, index) => (
                            <th
                                key={index}
                                className="border border-gray-300 text-center bg-indigo-50 px-1 py-2 text-gray-700 text-sm font-semibold w-[130px] min-w-[130px] max-w-[130px]"
                            >
                                {day}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr className='h-40'>
                        {daysOfWeek.map((day, index) => {
                            const shifts = shiftsByDay[day] || [];
                            const hasShifts = shifts.length > 0;
                            const whName = whNameByDay?.[day] || '';
                            const dailyTotal = shifts.reduce((sum, shift) => sum + getShiftDuration(shift), 0);

                            return (
                                <td
                                    key={index}
                                    className="border border-gray-200 text-sm px-1 pt-2 pb-0 w-[130px] min-w-[130px] max-w-[130px] h-40 align-top bg-white"
                                >
                                    {hasShifts ? (
                                        <div className='flex flex-col justify-between h-full'>
                                            <div className="flex flex-col">
                                                {shifts.map((shift, i) => (
                                                    <div key={i} className="mb-2">
                                                        <div className="flex justify-center items-center space-x-1 whitespace-nowrap">
                                                        <BriefcaseIcon className="w-4 h-4 text-indigo-700 shrink-0 mx-0.5" />
                                                        <span className='text-gray-800 font-semibold text-sm'>{shift.workStartTime} - {shift.workEndTime}</span>
                                                        </div>
                                                        {shift.breaks?.length > 0 && (
                                                            <div className="text-xs text-gray-600 mt-1 flex flex-col space-y-1">
                                                                {shift.breaks.map((p, j) => (
                                                                <div key={j} className={`flex items-center justify-center space-x-1 ${!p?.is_visible ? 'text-gray-400' : 'text-gray-600'}`}>
                                                                    {!p.is_visible ? (
                                                                        <EyeSlashIcon className="h-4 w-4 text-red-500" strokeWidth={2} />
                                                                    ) : (
                                                                        <LocalBarIcon style={{ height: '14px', width: '14px', color: '#4338ca', marginRight: '0.5px' }} />
                                                                    )}
                                                                    <span className='text-gray-800 font-normal'>{p.start_at} - {p.end_at}</span>
                                                                </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {i < shifts.length - 1 && <hr className="my-2 border-gray-300" />}
                                                    </div>
                                                ))}
                                                {whName && !readOnly && (
                                                    <CustomTooltipComponent title={whName} placement="bottom" arrow>
                                                        <div className="text-xs text-indigo-800 font-semibold my-2 text-center bg-indigo-50 rounded-xl mx-1 py-0.5 px-1 cursor-pointer">
                                                            <AttachFileOutlinedIcon
                                                            className="inline-block text-indigo-800 text-xs flex"
                                                            style={{ width: "15px", height: "15px" }}
                                                            /> Turno giorn.
                                                        </div>
                                                    </CustomTooltipComponent>
                                                )}
                                            </div>
                                            {!readOnly && (
                                                <div className="flex items-center justify-center gap-3 border-t mt-auto w-full pt-2">
                                                    {isCustomDayByDay?.[day] && (
                                                        <PencilSquareIcon
                                                            className="text-indigo-700 hover:text-indigo-600 cursor-pointer w-5 h-5"
                                                            title="Modifica fasce"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                onEditDay?.(day);
                                                            }}
                                                        />
                                                    )}

                                                    <TrashIcon
                                                        className="text-indigo-700 hover:text-indigo-600 cursor-pointer w-5 h-5"
                                                        title="Rimuovi"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            onCancelShift?.(day);
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            {dailyTotal > 0 && (
                                                <div className="text-center text-[11px] text-gray-600 mt-1">
                                                    Durata: {formatHoursAndMinutes(dailyTotal)}
                                                </div>
                                            )}
                                        </div>
                                    ) : readOnly ? (
                                      <div className="flex items-center justify-center h-full w-full text-xs text-gray-400">
                                        
                                      </div>
                                    ) : (
                                        <div className="flex items-center justify-center h-full w-full">
                                            <PlusCircleIcon
                                                className="text-indigo-500 hover:bg-indigo-50 rounded-full hover:text-indigo-600 hover:bg-indigo-400 cursor-pointer w-10 h-10 transition duration-300 hover:-translate-y-2 hover:scale-105 hover:bg-indigo-50"
                                                onClick={() => onPlusCircleClick(day)} 
                                            />
                                        </div>
                                    )}
                                </td>
                                
                            );
                        })}
                    </tr>
                </tbody>
                <tfoot>
                    <tr className="bg-white">
                        <td colSpan={daysOfWeek.length} className="p-0">
                            {readOnly ? (
                                <div className="flex items-center justify-between gap-3 bg-gray-50 px-4 py-2 border-t border-gray-200 rounded-b-xl">
                                    <div className="text-xs font-semibold uppercase text-gray-700">
                                        Totale ore settimanali
                                    </div>
                                    <div className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-[13px] font-semibold text-gray-800 border border-gray-200 uppercase">
                                        <span className="text-indigo-700">{formatHoursAndMinutes(totalWeeklyHours)}</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-left px-4 py-2 text-sm text-gray-600 font-semibold border-t border-gray-200">
                                    Totale ore settimanali: {formatHoursAndMinutes(totalWeeklyHours)}
                                </div>
                            )}
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

export default WeekCalendar;
